import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  MessageSquare,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Globe,
  ArrowLeft,
} from "lucide-react";
import api from "../api/axios";

export const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailErrorType, setEmailErrorType] = useState(null); // "invalidFormat" veya null
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, setUser, loadProjects } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  
  // Helper function to get legal document route URL
  const getLegalDocumentUrl = (type) => {
    return `/legal/${type}`;
  };
  
  // Email hata mesajını errorType'a göre çevir
  const getEmailErrorMessage = () => {
    if (emailErrorType === "invalidFormat") {
      return t.invalidEmailFormat || (language === "tr" 
        ? "Lütfen geçerli bir e-posta adresi girin"
        : "Please enter a valid email address");
    }
    return "";
  };

  const languageNames = {
    tr: "Türkçe",
    en: "English",
    de: "Deutsch",
    es: "Español",
    fr: "Français",
  };

  // Şifre validasyon fonksiyonu
  const validatePassword = (pwd) => {
    setPasswordValidation({
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    });
  };

  // Email validasyon fonksiyonu
  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (emailValue && !emailRegex.test(emailValue)) {
      setEmailErrorType("invalidFormat");
    } else {
      setEmailErrorType(null);
    }
  };

  // Email değiştiğinde validasyonu kontrol et
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    validateEmail(newEmail);
  };

  // Şifre değiştiğinde validasyonu kontrol et
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Email validasyonunu kontrol et
    validateEmail(email);
    if (emailErrorType === "invalidFormat") {
      const errorMsg = getEmailErrorMessage();
      toast.error(errorMsg);
      return;
    }

    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailErrorType("invalidFormat");
      const errorMsg = getEmailErrorMessage();
      toast.error(errorMsg);
      return;
    }

    // Şifre validasyonunu kontrol et
    const isPasswordValid = Object.values(passwordValidation).every(
      (rule) => rule === true
    );
    if (!isPasswordValid) {
      const errorMsg = t.passwordRequirementsNotMet || (language === "tr" 
        ? "Lütfen şifre gereksinimlerini karşılayın"
        : "Please meet the password requirements");
      toast.error(errorMsg);
      return;
    }

    // Terms and Privacy kontrolü
    if (!acceptTerms) {
      const errorMsg = t.termsAndPrivacyRequired || (language === "tr"
        ? "Kullanım Şartları ve Gizlilik Politikası'nı kabul etmelisiniz."
        : "You must accept the Terms of Service and Privacy Policy.");
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      // Pass language parameter to register function
      const result = await register(name, email, password, language);

      // Show success message
      toast.success(t.verificationEmailSent);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Registration error:", error);
      // Daha detaylı hata mesajı göster
      let errorMessage = t.registrationFailed || (language === "tr" 
        ? "Kayıt işlemi başarısız oldu."
        : "Registration failed.");

      if (!error.response) {
        // Network error - backend'e bağlanılamıyor
        errorMessage = t.backendConnectionError || (language === "tr"
          ? "Backend sunucusuna bağlanılamıyor. Lütfen backend'in çalıştığından emin olun."
          : "Cannot connect to backend server. Please make sure the backend is running.");
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Backend'den gelen mesajları çevir
        if (typeof detail === 'object') {
          if (Array.isArray(detail)) {
            errorMessage = detail[0]?.msg || detail[0]?.message || JSON.stringify(detail[0]) || errorMessage;
          } else if (detail.msg) {
            errorMessage = detail.msg;
          } else if (detail.message) {
            errorMessage = detail.message;
          } else {
            errorMessage = JSON.stringify(detail);
          }
        } else {
          errorMessage = detail;
        }
        
        // Email format hatası kontrolü
        const errorMessageLower = errorMessage.toLowerCase();
        if (errorMessageLower.includes("invalid email") ||
            errorMessageLower.includes("email format") ||
            errorMessageLower.includes("geçerli e-posta") ||
            errorMessageLower.includes("e-posta formatı")) {
          setEmailErrorType("invalidFormat");
          errorMessage = getEmailErrorMessage();
        } else if (errorMessageLower.includes("already exists") ||
                   errorMessageLower.includes("already registered") ||
                   errorMessageLower.includes("zaten kayıtlı") ||
                   errorMessageLower.includes("bereits registriert") ||
                   errorMessageLower.includes("ya está registrada") ||
                   errorMessageLower.includes("déjà enregistrée")) {
          errorMessage = t.emailAlreadyRegistered || (language === "tr"
            ? "Bu e-posta adresi zaten kayıtlı"
            : "This email address is already registered");
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);

      // Backend'den Google OAuth URL'ini al (mevcut dil bilgisini gönder)
      const response = await api.get(`/auth/google?hl=${language}`);
      const { authorization_url } = response.data;

      // Full page redirect - popup yerine doğrudan Google OAuth sayfasına yönlendir
      window.location.href = authorization_url;

    } catch (error) {
      console.error("Google login error:", error);
      toast.error(t.googleLoginError || "Google ile giriş başarısız. Lütfen tekrar deneyin.");
      setLoading(false);
    }
  };

  const RegisterContent = (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-3 md:p-4 relative">
      {/* Back Button - Sol üst köşe */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-3 md:top-4 left-3 md:left-4 p-2 hover:bg-white/20 dark:hover:bg-gray-800/50 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        aria-label="Go back to home"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Language Selector & Theme Toggle - Sağ üst köşe */}
      <div className="absolute top-3 md:top-4 right-3 md:right-4 flex items-center space-x-2 md:space-x-3">
        <ThemeToggle />
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-[80px] md:w-[140px] text-xs md:text-sm">
            <SelectValue>
              <div className="flex items-center space-x-1 md:space-x-2">
                <Globe className="w-3 md:w-4 h-3 md:h-4" />
                <span className="hidden md:inline">{languageNames[language] || languageNames.tr}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tr" className="text-xs md:text-sm">{languageNames.tr}</SelectItem>
            <SelectItem value="en" className="text-xs md:text-sm">{languageNames.en}</SelectItem>
            <SelectItem value="de" className="text-xs md:text-sm">{languageNames.de}</SelectItem>
            <SelectItem value="es" className="text-xs md:text-sm">{languageNames.es}</SelectItem>
            <SelectItem value="fr" className="text-xs md:text-sm">{languageNames.fr}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-6 md:mb-8">
          <img 
            src="/logo-256.webp" 
            alt="FeedPanels Logo" 
            className="w-36 md:w-40 h-36 md:h-40 object-contain mb-1 md:mb-2 mx-auto"
          />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            {t.createAccount || "Kayıt Ol"}
          </h1>
        </div>

        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg md:text-2xl dark:text-white" data-testid="register-title">
              {t.createAccount || "Create account"}
            </CardTitle>
            <CardDescription className="text-xs md:text-sm dark:text-gray-400">
              {t.getStarted || "Get started with your free account"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs md:text-sm dark:text-gray-300">{t.companyName || "Company Name"}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t.companyNamePlaceholder || "Acme Inc."}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="register-name-input"
                  className="text-xs md:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs md:text-sm dark:text-gray-300">{t.emailLabel || "Email"}</Label>
                <Input
                  id="email"
                  type="text"
                  inputMode="email"
                  placeholder={t.emailPlaceholder || "you@company.com"}
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => validateEmail(email)}
                  required
                  data-testid="register-email-input"
                  className={`text-xs md:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 ${emailErrorType ? "border-destructive" : ""}`}
                />
                {emailErrorType && (
                  <p className="text-xs md:text-sm text-destructive dark:text-red-400 mt-1">{getEmailErrorMessage()}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs md:text-sm dark:text-gray-300">
                  {t.passwordLabel || "Password"}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                    data-testid="register-password-input"
                    className="pr-10 text-xs md:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                    aria-label={
                      showPassword ? "Şifreyi gizle" : "Şifreyi göster"
                    }
                  >
                    {showPassword ? <Eye size={16} className="md:w-[18px]" /> : <EyeOff size={16} className="md:w-[18px]" />}
                  </button>
                </div>
                {/* Şifre Validasyon Mesajları */}
                {password && (
                  <div className="mt-2 space-y-1 md:space-y-1.5 text-xs md:text-sm">
                    <div
                      className={`flex items-center gap-2 ${
                        passwordValidation.minLength
                          ? "text-green-600"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {passwordValidation.minLength ? (
                        <CheckCircle2 size={14} className="flex-shrink-0 md:w-4" />
                      ) : (
                        <XCircle size={14} className="flex-shrink-0 md:w-4" />
                      )}
                      <span>{t.minLength || "At least 8 characters"}</span>
                    </div>
                    <div
                      className={`flex items-center gap-2 ${
                        passwordValidation.hasUpperCase
                          ? "text-green-600"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {passwordValidation.hasUpperCase ? (
                        <CheckCircle2 size={14} className="flex-shrink-0 md:w-4" />
                      ) : (
                        <XCircle size={14} className="flex-shrink-0 md:w-4" />
                      )}
                      <span>
                        {t.hasUpperCase || "At least one uppercase letter"}
                      </span>
                    </div>
                    <div
                      className={`flex items-center gap-2 ${
                        passwordValidation.hasLowerCase
                          ? "text-green-600"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {passwordValidation.hasLowerCase ? (
                        <CheckCircle2 size={14} className="flex-shrink-0 md:w-4" />
                      ) : (
                        <XCircle size={14} className="flex-shrink-0 md:w-4" />
                      )}
                      <span>
                        {t.hasLowerCase || "At least one lowercase letter"}
                      </span>
                    </div>
                    <div
                      className={`flex items-center gap-2 ${
                        passwordValidation.hasNumber
                          ? "text-green-600"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {passwordValidation.hasNumber ? (
                        <CheckCircle2 size={14} className="flex-shrink-0 md:w-4" />
                      ) : (
                        <XCircle size={14} className="flex-shrink-0 md:w-4" />
                      )}
                      <span>{t.hasNumber || "At least one number"}</span>
                    </div>
                    <div
                      className={`flex items-center gap-2 ${
                        passwordValidation.hasSpecialChar
                          ? "text-green-600"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {passwordValidation.hasSpecialChar ? (
                        <CheckCircle2 size={14} className="flex-shrink-0 md:w-4" />
                      ) : (
                        <XCircle size={14} className="flex-shrink-0 md:w-4" />
                      )}
                      <span>
                        {t.hasSpecialChar || "At least one special character"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Terms and Privacy Checkbox */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500 dark:focus:ring-cyan-400 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0"
                    required
                    data-testid="accept-terms-checkbox"
                  />
                  <label
                    htmlFor="acceptTerms"
                    className="text-xs md:text-sm text-gray-700 dark:text-gray-300 cursor-pointer leading-relaxed"
                  >
                    {language === 'tr' && (
                      <>
                        <Link
                          to={getLegalDocumentUrl('legal')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Kullanım Şartları
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> ve </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Gizlilik Politikası
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400">'nı kabul ediyorum.</span>
                      </>
                    )}
                    {language === 'en' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">I accept the </span>
                        <Link
                          to={getLegalDocumentUrl('legal')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms of Service
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> and </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400">.</span>
                      </>
                    )}
                    {language === 'de' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">Ich akzeptiere die </span>
                        <Link
                          to={getLegalDocumentUrl('legal')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Nutzungsbedingungen
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> und die </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Datenschutzerklärung
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400">.</span>
                      </>
                    )}
                    {language === 'es' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">Acepto los </span>
                        <Link
                          to={getLegalDocumentUrl('legal')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Términos de Servicio
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> y la </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Política de Privacidad
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400">.</span>
                      </>
                    )}
                    {language === 'fr' && (
                      <>
                        <span className="text-gray-600 dark:text-gray-400">J'accepte les </span>
                        <Link
                          to={getLegalDocumentUrl('legal')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Conditions d'Utilisation
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400"> et la </span>
                        <Link
                          to={getLegalDocumentUrl('privacy')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Politique de Confidentialité
                        </Link>
                        <span className="text-gray-600 dark:text-gray-400">.</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full text-xs md:text-sm mt-2 md:mt-0"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading
                  ? t.creatingAccount || "Creating account..."
                  : t.createAccount || "Create Account"}
              </Button>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">
                    {t.orContinueWith || "Or continue with"}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full text-xs md:text-sm"
                disabled={loading}
                onClick={handleGoogleLogin}
                data-testid="google-register-button"
              >
                <svg className="mr-2 h-3 md:h-4 w-3 md:w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t.googleSignUp || "Sign up with Google"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2 dark:border-t dark:border-gray-700">
            <div className="text-xs md:text-sm text-center text-gray-600 dark:text-gray-400">
              {t.alreadyHaveAccount || "Already have an account?"}{" "}
              <Link
                to="/login"
                className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                data-testid="login-link"
              >
                {t.signIn || "Sign in"}
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Sign Up - FeedPanels</title>
        <meta name="description" content="Create a free FeedPanels account. Start collecting customer feedback, build your roadmap, and publish changelogs." />
        <link rel="canonical" href="https://feedpanels.com/register" />
      </Helmet>
      {RegisterContent}
    </>
  );
};
